import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { 
  Target, 
  Calculator, 
  Users, 
  DollarSign,
  TrendingUp,
  Save,
  AlertCircle,
  CheckCircle,
  BarChart3
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import axios from "axios";

// Use centralized API base to avoid CORS and hardcoding
const API = require('../services/apiBase').default || require('../services/apiBase');

const ThreeFactorFormula = ({ calculationId, businessUnits, onFormulaUpdate, onResultsUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Three-factor formula state
  const [formula, setFormula] = useState({
    headcount_weight: 33.33,
    payroll_weight: 33.33,
    revenue_weight: 33.34,
    total_residual_costs: 0,
    business_units: [],
    allocation_results: null
  });

  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);

  useEffect(() => {
    if (calculationId) {
      loadThreeFactorFormula();
    }
  }, [calculationId]);

  useEffect(() => {
    // Auto-select all business units when they're available
    if (businessUnits && businessUnits.length > 0) {
      const unitIds = businessUnits.map(unit => unit.id);
      setSelectedBusinessUnits(unitIds);
      setFormula(prev => ({ ...prev, business_units: unitIds }));
    }
  }, [businessUnits]);

  const loadThreeFactorFormula = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/three-factor-formula/${calculationId}`);
      
      if (response.data.has_formula) {
        const existingFormula = response.data.formula;
        setFormula({
          headcount_weight: existingFormula.headcount_weight || 33.33,
          payroll_weight: existingFormula.payroll_weight || 33.33,
          revenue_weight: existingFormula.revenue_weight || 33.34,
          total_residual_costs: existingFormula.total_residual_costs || 0,
          business_units: existingFormula.business_units || [],
          allocation_results: existingFormula.allocation_results || null
        });
        setSelectedBusinessUnits(existingFormula.business_units || []);
        
        if (onFormulaUpdate) {
          onFormulaUpdate(existingFormula);
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error loading three-factor formula");
    } finally {
      setLoading(false);
    }
  };

  const validateWeights = () => {
    const total = parseFloat(formula.headcount_weight) + 
                 parseFloat(formula.payroll_weight) + 
                 parseFloat(formula.revenue_weight);
    return Math.abs(total - 100.0) <= 0.01;
  };

  const handleSaveFormula = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Validate weightings
    if (!validateWeights()) {
      setError("Weightings must sum to exactly 100%");
      setSaving(false);
      return;
    }

    // Validate business units
    if (selectedBusinessUnits.length === 0) {
      setError("Please select at least one business unit");
      setSaving(false);
      return;
    }

    // Validate residual costs
    if (!formula.total_residual_costs || formula.total_residual_costs <= 0) {
      setError("Please enter a valid residual cost amount");
      setSaving(false);
      return;
    }

    try {
      // Attach business unit metrics so backend can compute
      const selectedMetrics = (businessUnits || [])
        .filter(u => selectedBusinessUnits.includes(u.id))
        .map(u => ({
          id: u.id,
          unit_name: u.unit_name || u.name || String(u.id),
          headcount: u.headcount || 0,
          annual_payroll: u.annual_payroll || 0,
          annual_revenue: u.annual_revenue || 0,
        }));

      const formulaData = {
        calculation_id: calculationId,
        headcount_weight: parseFloat(formula.headcount_weight),
        payroll_weight: parseFloat(formula.payroll_weight),
        revenue_weight: parseFloat(formula.revenue_weight),
        total_residual_costs: parseFloat(formula.total_residual_costs),
        business_units: selectedBusinessUnits,
        business_unit_metrics: selectedMetrics,
        created_by: "user"
      };

      const response = await axios.post(`${API}/three-factor-formula`, formulaData);

      if (response.data.success) {
        const allocationResults = response.data.allocation_results;
        setFormula(prev => ({ ...prev, allocation_results: allocationResults }));
        
        setSuccess("Three-factor formula saved and calculated successfully!");
        
        if (onFormulaUpdate) {
          onFormulaUpdate(response.data.formula);
        }
        
        if (onResultsUpdate && allocationResults) {
          onResultsUpdate(allocationResults);
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving three-factor formula");
    } finally {
      setSaving(false);
    }
  };

  const handleWeightChange = (weightType, value) => {
    const newWeight = parseFloat(value) || 0;
    
    // Auto-adjust other weights to maintain 100% total
    const otherWeights = Object.keys(formula).filter(key => 
      key.includes('_weight') && key !== `${weightType}_weight`
    );
    
    const remainingWeight = 100 - newWeight;
    const weightPerOther = remainingWeight / otherWeights.length;
    
    setFormula(prev => {
      const updated = { ...prev, [`${weightType}_weight`]: newWeight };
      
      if (otherWeights.length > 0) {
        otherWeights.forEach(otherKey => {
          updated[otherKey] = parseFloat(weightPerOther.toFixed(2));
        });
      }
      
      return updated;
    });
  };

  const getSelectedBusinessUnitsData = () => {
    return businessUnits.filter(unit => selectedBusinessUnits.includes(unit.id));
  };

  const formatCurrency = (value) => {
    return value ? `$${Number(value).toLocaleString()}` : '$0';
  };

  const renderAllocationResults = () => {
    if (!formula.allocation_results || !formula.allocation_results.allocation_breakdown) {
      return null;
    }

    const results = formula.allocation_results.allocation_breakdown;
    const summary = formula.allocation_results.calculation_summary;

    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            <span>Allocation Results</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-900">Total Allocated</div>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(formula.allocation_results.total_allocated)}
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-green-900">Total Headcount</div>
              <div className="text-2xl font-bold text-green-600">
                {summary?.total_headcount || 0}
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-900">Total Payroll</div>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(summary?.total_payroll)}
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-orange-900">Total Revenue</div>
              <div className="text-2xl font-bold text-orange-600">
                {formatCurrency(summary?.total_revenue)}
              </div>
            </div>
          </div>

          <Separator className="mb-6" />

          {/* Detailed Results Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business Unit</TableHead>
                  <TableHead className="text-right">Headcount Factor</TableHead>
                  <TableHead className="text-right">Payroll Factor</TableHead>
                  <TableHead className="text-right">Revenue Factor</TableHead>
                  <TableHead className="text-right">Weighted Allocation</TableHead>
                  <TableHead className="text-right">Allocated Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((result) => (
                  <TableRow key={result.business_unit_id}>
                    <TableCell className="font-medium">
                      {result.business_unit_name}
                    </TableCell>
                    <TableCell className="text-right">
                      {(result.headcount_factor * 100).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right">
                      {(result.payroll_factor * 100).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right">
                      {(result.revenue_factor * 100).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {(result.weighted_allocation * 100).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right font-bold text-blue-600">
                      {formatCurrency(result.allocated_amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Weighting Summary */}
          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium mb-2">Applied Weightings</h4>
            <div className="text-sm text-slate-600">
              Headcount: {summary?.weightings?.headcount}% | 
              Payroll: {summary?.weightings?.payroll}% | 
              Revenue: {summary?.weightings?.revenue}%
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading three-factor formula...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-red-700">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-700">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* Three-Factor Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-blue-600" />
            <span>Three-Factor Formula Configuration</span>
          </CardTitle>
          <div className="text-sm text-slate-600">
            Configure residual cost allocation based on headcount, payroll, and revenue factors
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Weightings Configuration */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center space-x-2">
              <Calculator className="h-4 w-4" />
              <span>Factor Weightings (%)</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="headcount_weight">Headcount Weight (%)</Label>
                <Input
                  id="headcount_weight"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formula.headcount_weight}
                  onChange={(e) => handleWeightChange('headcount', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="payroll_weight">Payroll Weight (%)</Label>
                <Input
                  id="payroll_weight"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formula.payroll_weight}
                  onChange={(e) => handleWeightChange('payroll', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="revenue_weight">Revenue Weight (%)</Label>
                <Input
                  id="revenue_weight"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formula.revenue_weight}
                  onChange={(e) => handleWeightChange('revenue', e.target.value)}
                />
              </div>
            </div>
            
            {/* Weight Validation Display */}
            <div className="mt-2 flex items-center space-x-2">
              {validateWeights() ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">
                    Total: {(parseFloat(formula.headcount_weight) + parseFloat(formula.payroll_weight) + parseFloat(formula.revenue_weight)).toFixed(2)}% ✓
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-600">
                    Total: {(parseFloat(formula.headcount_weight) + parseFloat(formula.payroll_weight) + parseFloat(formula.revenue_weight)).toFixed(2)}% (must equal 100%)
                  </span>
                </>
              )}
            </div>
          </div>

          <Separator />

          {/* Residual Costs */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center space-x-2">
              <DollarSign className="h-4 w-4" />
              <span>Residual Costs to Allocate</span>
            </h4>
            <div className="max-w-md">
              <Label htmlFor="total_residual_costs">Total Residual Costs ($)</Label>
              <Input
                id="total_residual_costs"
                type="number"
                step="0.01"
                min="0"
                placeholder="1000000.00"
                value={formula.total_residual_costs}
                onChange={(e) => setFormula(prev => ({ ...prev, total_residual_costs: e.target.value }))}
              />
              <div className="text-xs text-slate-500 mt-1">
                Enter the total amount of residual costs to be allocated across business units
              </div>
            </div>
          </div>

          <Separator />

          {/* Business Unit Selection */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Business Unit Selection</span>
            </h4>
            
            {businessUnits.length === 0 ? (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-yellow-800">
                  No business units available. Please create business units in the Business Units tab first.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {businessUnits.map((unit) => (
                  <div key={unit.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedBusinessUnits.includes(unit.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBusinessUnits(prev => [...prev, unit.id]);
                          } else {
                            setSelectedBusinessUnits(prev => prev.filter(id => id !== unit.id));
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <div>
                        <div className="font-medium">{unit.unit_name}</div>
                        <div className="text-sm text-slate-500">Code: {unit.unit_code}</div>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div>HC: {unit.headcount || 0}</div>
                      <div>Payroll: {formatCurrency(unit.annual_payroll)}</div>
                      <div>Revenue: {formatCurrency(unit.annual_revenue)}</div>
                    </div>
                  </div>
                ))}
                
                <div className="text-sm text-slate-600 mt-3">
                  Selected {selectedBusinessUnits.length} of {businessUnits.length} business units
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveFormula}
              disabled={saving || !validateWeights() || selectedBusinessUnits.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Calculating..." : "Save & Calculate Allocations"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Allocation Results */}
      {renderAllocationResults()}
    </div>
  );
};

export default ThreeFactorFormula;